import {
  Box,
  SwitchIndicator,
  TextField,
  Select,
  FieldData,
  useForm,
  SubAccountPermission,
  withStyles,
  MaterialSymbol,
} from 'vopay';

import { ChangeEvent, useImperativeHandle, useMemo, useEffect, Ref, FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BodyText } from 'vopay';
import { Title, UtilButton } from 'UI';
import CollapsibleList from '@/components/Users/UserPermissions/CollapsibleList';
import styles from './AccountPermissionForm.module.scss';

const classes = withStyles(styles);
type PermissionFormValues = {
  [key: string]: boolean | string | number;
};

type AccountPermissionFormProps = {
  onAccountPermissionSave: (values: PermissionFormValues) => void;
  permissions: SubAccountPermission[];
  ref: Ref<AccountPermissionFormRef>;
  onFormStateChange: (hasChanges: boolean, isValid: boolean) => void;
  onSearchChange: (isSearching: boolean) => void;
};

export type AccountPermissionFormRef = {
  enableAll: () => void;
};

type ToggleProps = {
  name: string;
  checked: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
};

const Toggle: FC<ToggleProps> = ({ name, checked, onChange }) => {
  return (
    <>
      <SwitchIndicator checked={checked} />
      <input
        className={classes('visuallyHidden')}
        id={`checkbox-${name}`}
        type="checkbox"
        name={name}
        checked={checked}
        onChange={onChange}
        data-checked={checked}
      />
    </>
  );
};

// Group permissions by Section
function groupPermissionsBySection(permissions: SubAccountPermission[]): Record<string, SubAccountPermission[]> {
  const grouped: Record<string, SubAccountPermission[]> = {};

  permissions.forEach((permission) => {
    const section = permission.Section || 'Other';
    if (!grouped[section]) {
      grouped[section] = [];
    }
    grouped[section].push(permission);
  });

  return grouped;
}

type AccountPermissionFieldData = Exclude<FieldData<PermissionFormValues>, 'label'> & {
  label: () => string;
};

function buildPermissionsConfig(permissions: SubAccountPermission[]): Record<string, AccountPermissionFieldData[]> {
  const config: Record<string, AccountPermissionFieldData[]> = {};
  const grouped = groupPermissionsBySection(permissions);

  Object.entries(grouped).forEach(([sectionName, sectionPermissions]) => {
    const groupFields: AccountPermissionFieldData[] = [];

    sectionPermissions.forEach((permission) => {
      groupFields.push({
        fieldName: permission.Permission,
        label: () => permission.Label,
        getInputState: () => 'optional',
        showField: () => true,
        serializeField: (values) =>
          permission.Type === 'number' ? Number(values[permission.Permission]) : values[permission.Permission],
        renderField: ({ getFormikData }) => {
          if (permission.Type === 'boolean') {
            const fieldData = getFormikData<boolean>({});
            return <Toggle name={fieldData.name} checked={!!fieldData.value} onChange={fieldData.onChange} />;
          } else if (permission.Type === 'enum' && permission.ExtraFields?.EnumValues) {
            const fieldData = getFormikData<string>({});
            const enumValues = permission.ExtraFields.EnumValues || [];
            const options = enumValues.map((enumValue) => ({
              label: enumValue,
              value: enumValue,
            }));
            return (
              <Select
                version="2"
                name={fieldData.name}
                value={fieldData.value}
                onBlur={fieldData.onBlur}
                onChange={fieldData.onChange}
                options={options}
              />
            );
          } else if (permission.Type === 'number') {
            const fieldData = getFormikData<number>({});
            return (
              <TextField
                variant="outlineShadow"
                preset="number"
                name={fieldData.name}
                value={String(fieldData.value)}
                onBlur={fieldData.onBlur}
                onChange={fieldData.onChange}
              />
            );
          } else {
            // string type
            const fieldData = getFormikData<string>({});
            return (
              <TextField
                variant="outlineShadow"
                name={fieldData.name}
                value={fieldData.value}
                onBlur={fieldData.onBlur}
                onChange={fieldData.onChange}
              />
            );
          }
        },
      });
    });

    config[sectionName] = groupFields;
  });

  return config;
}

function buildInitialValues(permissions: SubAccountPermission[]): PermissionFormValues {
  return permissions.reduce((acc, permission) => {
    return {
      ...acc,
      [permission.Permission]: permission.Type === 'number' ? String(permission.Value) : permission.Value,
    };
  }, {});
}

// Check if a section has any boolean fields
function sectionHasBooleanFields(sectionName: string, permissions: SubAccountPermission[]): boolean {
  const grouped = groupPermissionsBySection(permissions);
  const sectionPermissions = grouped[sectionName] || [];
  return sectionPermissions.some((p) => p.Type === 'boolean');
}

// Format group name from kebab-case to Title Case
function formatGroupName(groupName: string): string {
  return groupName
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

const AccountPermissionForm: FC<AccountPermissionFormProps> = ({
  onAccountPermissionSave,
  permissions,
  onFormStateChange,
  onSearchChange,
  ref,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.Permissions.form' });
  const [searchTerm, setSearchTerm] = useState('');

  const initialValues = useMemo(() => buildInitialValues(permissions), [permissions]);
  const permissionsConfig = useMemo(() => buildPermissionsConfig(permissions), [permissions]);

  const { ctx, renderFormElements } = useForm<PermissionFormValues>({
    config: Object.values(permissionsConfig).flat(),
    initialValues,
    onSubmit: (values) => {
      onAccountPermissionSave(values);
    },
  });

  // Check if form has changes and is valid
  const hasChanges = useMemo(() => {
    return JSON.stringify(ctx.values) !== JSON.stringify(initialValues);
  }, [ctx.values, initialValues]);

  // Notify parent of form state changes
  useEffect(() => {
    if (onFormStateChange) {
      onFormStateChange(hasChanges, ctx.isValid);
    }
  }, [hasChanges, ctx.isValid, onFormStateChange]);

  // Notify parent when search state changes
  useEffect(() => {
    if (onSearchChange) {
      onSearchChange(searchTerm.trim().length > 0);
    }
  }, [searchTerm, onSearchChange]);

  // Track how many boolean permissions are enabled per group
  const groupStats = useMemo(() => {
    const stats: Record<string, { enabled: number; total: number }> = {};
    const grouped = groupPermissionsBySection(permissions);

    Object.entries(permissionsConfig).forEach(([groupName]) => {
      const sectionPermissions = grouped[groupName] || [];
      const booleanPermissions = sectionPermissions.filter((p) => p.Type === 'boolean');
      const total = booleanPermissions.length;
      const enabled = booleanPermissions.filter((p) => ctx.values[p.Permission] === true).length;
      stats[groupName] = { enabled, total };
    });

    return stats;
  }, [permissionsConfig, ctx.values, permissions]);

  // Function to enable all boolean permissions in a specific group
  const enableAllInGroup = (groupName: string) => {
    const grouped = groupPermissionsBySection(permissions);
    const sectionPermissions = grouped[groupName] || [];

    const updates: Record<string, boolean> = {};
    sectionPermissions.forEach((permission) => {
      if (permission.Type === 'boolean') {
        updates[permission.Permission] = true;
      }
    });
    ctx.setValues({ ...ctx.values, ...updates });
  };

  // Function to disable all boolean permissions in a specific group
  const disableAllInGroup = (groupName: string) => {
    const grouped = groupPermissionsBySection(permissions);
    const sectionPermissions = grouped[groupName] || [];

    const updates: Record<string, boolean> = {};
    sectionPermissions.forEach((permission) => {
      if (permission.Type === 'boolean') {
        updates[permission.Permission] = false;
      }
    });
    ctx.setValues({ ...ctx.values, ...updates });
  };

  // Function to enable all boolean permissions
  const enableAll = () => {
    const updates: Record<string, boolean> = {};
    permissions.forEach((permission) => {
      if (permission.Type === 'boolean') {
        updates[permission.Permission] = true;
      }
    });
    ctx.setValues({ ...ctx.values, ...updates });
  };

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    enableAll,
  }));

  // Filter permissions based on search term
  const filteredPermissionsConfig = useMemo(() => {
    if (!searchTerm.trim()) {
      return permissionsConfig;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    const filtered: Record<string, FieldData<PermissionFormValues>[]> = {};

    Object.entries(permissionsConfig).forEach(([groupName, groupFields]) => {
      const groupMatches = groupName.toLowerCase().includes(lowerSearchTerm);

      // Filter permissions within the group by checking their labels
      const matchingPermissions = groupFields.filter((field) => {
        const label = field.label?.() || '';

        return String(label).toLowerCase().includes(lowerSearchTerm);
      });

      if (groupMatches || matchingPermissions.length > 0) {
        filtered[groupName] = groupMatches ? groupFields : matchingPermissions;
      }
    });

    return filtered;
  }, [permissionsConfig, searchTerm]);

  const hasFilteredResults = Object.keys(filteredPermissionsConfig).length > 0;
  const isFiltering = searchTerm.trim().length > 0;

  return (
    <form onSubmit={ctx.handleSubmit} id="accountPermissionForm" className={classes('formContainer')}>
      <Box mb="md" base="8" flx="col" className={classes('searchBox')}>
        <TextField
          name="permissionSearch"
          placeholder={t('searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlineShadow"
          elementLeft={
            <div className={classes('searchIconWrapper')}>
              <MaterialSymbol icon="search" size="24px" />
            </div>
          }
        />
      </Box>

      {!hasFilteredResults && searchTerm.trim() ? (
        <div className={classes('emptyStateWrapper')}>
          <Box flx="col" ai="center" jc="center" gap="sm">
            <MaterialSymbol icon="manage_search" size="90px" color="rgba(45, 45, 45, 0.2)" />
            <Title level="4">{t('permissionNotFound')}</Title>
          </Box>
        </div>
      ) : (
        <CollapsibleList>
          {Object.entries(filteredPermissionsConfig).map(([groupName, groupFields]) => {
            const stats = groupStats[groupName];
            const hasBooleanFields = sectionHasBooleanFields(groupName, permissions);
            const allEnabled = hasBooleanFields && stats.total > 0 && stats.enabled === stats.total;

            return (
              <CollapsibleList.Item key={groupName} defaultOpen={false} open={isFiltering}>
                <CollapsibleList.Trigger>
                  <div className={classes('groupHeader')}>
                    <div className={classes('groupInfo')}>
                      <Title level="5">{formatGroupName(groupName)}</Title>
                      {hasBooleanFields && stats.total > 0 && (
                        <BodyText weight="rg" className={classes('groupCount')}>
                          ({stats.enabled}/{stats.total})
                        </BodyText>
                      )}
                    </div>
                    {hasBooleanFields && !isFiltering && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <div className={classes('groupActions')}>
                          {allEnabled ? (
                            <UtilButton size="medium" onClick={() => disableAllInGroup(groupName)}>
                              {t('disableAll')}
                            </UtilButton>
                          ) : (
                            <UtilButton size="medium" onClick={() => enableAllInGroup(groupName)}>
                              {t('enableAll')}
                            </UtilButton>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </CollapsibleList.Trigger>

                <CollapsibleList.Content>
                  <Box flx="col" gap="md" base="8">
                    {renderFormElements({
                      config: groupFields,
                      override: (fieldData) => {
                        const permission = permissions.find((p) => p.Permission === fieldData.fieldName);
                        const isBoolean = permission?.Type === 'boolean';

                        return (
                          <label
                            className={classes('permissionLabel', isBoolean ? 'clickable' : 'default')}
                            htmlFor={isBoolean ? `checkbox-${fieldData.fieldName}` : undefined}
                          >
                            <BodyText className={classes('permissionLabelText')} weight="md">
                              {fieldData.label}
                            </BodyText>

                            <div
                              data-testid={`toggle-${fieldData.fieldName}`}
                              className={classes('permissionFieldWrapper')}
                            >
                              {fieldData.template}
                            </div>
                          </label>
                        );
                      },
                    })}
                  </Box>
                </CollapsibleList.Content>
              </CollapsibleList.Item>
            );
          })}
        </CollapsibleList>
      )}
    </form>
  );
};

export default AccountPermissionForm;
